import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";

/** Ein Formular, das an der gemeinsamen Speichern-Leiste hängt. */
interface FormEntry {
  label: string;
  /** Speichert über den bisherigen Endpunkt; false = fehlgeschlagen (Fehler zeigt das Formular selbst). */
  save: () => Promise<boolean>;
  discard: () => void;
}

interface Registry {
  set: (id: string, entry: FormEntry | null) => void;
}

const SaveBarContext = createContext<Registry | null>(null);

/**
 * Hält alle Formulare einer Seite zusammen. Sobald eines ungespeicherte
 * Änderungen hat, erscheint oben die Speichern-Leiste; „Speichern“ speichert
 * jedes geänderte Formular nacheinander über seinen eigenen Aufruf.
 */
export function SaveBarProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<Map<string, FormEntry>>(new Map());
  const [saving, setSaving] = useState(false);

  const set = useCallback((id: string, entry: FormEntry | null) => {
    setEntries((prev) => {
      // Nichts geändert → gleicher State, kein erneutes Rendern.
      if (!entry && !prev.has(id)) return prev;
      const next = new Map(prev);
      if (entry) next.set(id, entry);
      else next.delete(id);
      return next;
    });
  }, []);
  // Stabiler Kontextwert, sonst melden sich die Formulare bei jedem Render neu an.
  const registry = useMemo(() => ({ set }), [set]);

  const dirty = [...entries.values()];

  async function saveAll() {
    setSaving(true);
    try {
      for (const e of dirty) await e.save();
    } finally {
      setSaving(false);
    }
  }

  return (
    <SaveBarContext.Provider value={registry}>
      <SaveBar
        visible={dirty.length > 0}
        saving={saving}
        message={
          dirty.length === 1
            ? `Nicht gespeichert: ${dirty[0].label}`
            : `Nicht gespeichert: ${dirty.map((d) => d.label).join(", ")}`
        }
        onSave={saveAll}
        onDiscard={() => dirty.forEach((e) => e.discard())}
      />
      {children}
    </SaveBarContext.Provider>
  );
}

/**
 * Meldet ein Formular an der Speichern-Leiste an (und beim Wächter für das
 * Verlassen der Seite). Ohne SaveBarProvider passiert nur Letzteres.
 */
export function useSaveBarForm({
  label,
  dirty,
  save,
  discard,
}: {
  label: string;
  dirty: boolean;
} & FormEntry) {
  const registry = useContext(SaveBarContext);
  const id = useId();
  // Immer die aktuellen Funktionen aufrufen, ohne bei jedem Render neu zu registrieren.
  const fns = useRef({ save, discard });
  fns.current = { save, discard };

  useUnsavedChanges(dirty);

  useEffect(() => {
    if (!registry) return;
    registry.set(
      id,
      dirty
        ? {
            label,
            save: () => fns.current.save(),
            discard: () => fns.current.discard(),
          }
        : null,
    );
  }, [registry, id, label, dirty]);

  useEffect(() => () => registry?.set(id, null), [registry, id]);
}

/** Leiste oben unter der Topbar, solange es ungespeicherte Änderungen gibt. */
export function SaveBar({
  visible,
  saving,
  onSave,
  onDiscard,
  message = "Nicht gespeicherte Änderungen",
}: {
  visible: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  message?: string;
}) {
  if (!visible) return null;
  return (
    <div
      role="region"
      aria-label="Ungespeicherte Änderungen"
      className="sticky top-(--header-height) z-20 -mx-4 -mt-5 mb-4 flex items-center gap-3 bg-topbar px-4 py-2 text-topbar-foreground shadow-card md:-mx-6 md:px-6"
    >
      <TriangleAlert className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate font-medium" title={message}>
        {message}
      </span>
      {onDiscard && (
        <Button
          size="sm"
          variant="ghost"
          className="text-topbar-foreground hover:bg-white/10 hover:text-white"
          onClick={onDiscard}
          disabled={saving}
        >
          Verwerfen
        </Button>
      )}
      <Button
        size="sm"
        className="bg-white text-primary hover:bg-white/90"
        onClick={onSave}
        disabled={saving}
      >
        {saving && <Loader2 className="animate-spin" />}
        {saving ? "Speichert…" : "Speichern"}
      </Button>
    </div>
  );
}
